// 批次 19（B8-1，Gitea issue #14）：dashboard 頁型的共用 modal 群（6 個角色首頁變體共用）。
//
// 對照 legacy modal JSP（WEB-INF/views/main/modal/*.jsp＋addCalendarModal.jsp）：
//   - announcementModal（modal/announce.jsp:146-219＋showAnnounce :87-115）
//   - todoFormModal（modal/todoForm.jsp:112-145：待填表單 5 欄＋列點擊分流 :90-110）
//   - coreSkillFormModal（modal/coreSkillForms.jsp:106-127：臨床核心技能表單 4 可見欄＋score 隱藏排序）
//   - gpaFormModal（modal/gpaForms.jsp:60-97：等第制表單 5 欄；**footer「Close」英文照實**）
//   - epaFormModal（modal/epaForms.jsp:79-107：EPA表單 5 欄）
//   - addCalendarModal（addCalendarModal.jsp:308-377：新增/修改個人行程 9 欄＋加入/移除＋
//     關閉/刪除/存檔/婉拒/接受；saveSchedule :200-232）
// 表單流程欄（genFormFlow，mainUtils.js:32-58）：`→<br>` 串接、第一個未完成節點紅字加 ★、
// 無帳號者顯示 待指定(角色)。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { Table, type TableColumn, type TableRow } from '@/components/base/Table';
import { Textarea } from '@/components/base/Textarea';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { LIGHT_COLOR_CLASS } from './dashboardParts';
import type {
  DashboardAnnouncementDetail,
  DashboardCoreSkillForm,
  DashboardEpaGradeBean,
  DashboardGradeReportRow,
  DashboardMaPlanForm,
  DashboardMySchedule,
  DashboardTodoForm,
  EpaFormReport,
  GpaFormReport,
  TeacherStudentDetail,
} from '@/mocks/dashboard-store';

/** EPA 分數條色（legacy css score-1..score-5；studentEpaGrade.jsp:87-121 未擷，近似色）
 *  【雛形值待實機校準】。 */
const SCORE_BAR_COLOR: Record<number, string> = {
  1: '#d9534f',
  2: '#f0ad4e',
  3: '#f7e28b',
  4: '#8bc4ea',
  5: '#5cb85c',
};

/** 表單流程節點串（mainUtils.js:32 genFormFlow 的消費形狀）。 */
export type DashboardFlowList = readonly {
  done: boolean;
  added?: boolean;
  flowRoleName: string;
  accountName?: string;
}[];

/**
 * 表單流程（mainUtils.js:32-58 genFormFlow 逐行鏡像：`→<br>` 串接；第一個 done=false
 * 節點前加 `<span class="text-danger">★`（紅字）並在該節點後收口；accountName 空＝
 * `待指定(flowRoleName)`，否則 `accountName(flowRoleName)`；added=true 時角色名改「加會」）。
 */
export function genFormFlow(flowList: DashboardFlowList): string {
  let result = '';
  let meetNotDone = false;
  let isFirst = true;
  for (const r of flowList) {
    if (!isFirst) {
      result += '→<br>';
    }
    isFirst = false;
    let thisFlowStr: string;
    const roleName = r.added === true ? '加會' : r.flowRoleName;
    if (r.accountName === undefined || r.accountName === '') {
      thisFlowStr = `待指定(${roleName})`;
    } else {
      thisFlowStr = `${r.accountName}(${roleName})`;
    }
    if (r.done === false && meetNotDone === false) {
      result += '<span class="text-danger">★';
    }
    result += thisFlowStr;
    if (r.done === false && meetNotDone === false) {
      result += '</span>';
      meetNotDone = true;
    }
  }
  return result;
}

/** genFormFlow 的 DOM 版（dangerouslySetInnerHTML；legacy 以字串塞 HTML，雛形同語意）。 */
export function FormFlowText({ flowList }: { flowList: DashboardFlowList }): ReactElement {
  return <span dangerouslySetInnerHTML={{ __html: genFormFlow(flowList) }} />;
}

// ---------------------------------------------------------------------------
// 公告內容 modal（announcementModal）
// ---------------------------------------------------------------------------

export interface AnnounceModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly detail: DashboardAnnouncementDetail | null;
  readonly onClose: () => void;
}

/**
 * 公告內容（modal/announce.jsp:146-219）：標題＝subject（h4 dialogModalLabel）；期間＝
 * `startDateStr ~ endDateStr`（annoDate）；公告單位／公告對象／相關網址（空＝「無」）／
 * 公告內容（sanitize 後 render——雛形以純文字呈現，D19-x 註記）；附件表（種子無附件）；
 * footer 關閉。
 */
export function AnnounceModal({ pageId, open, detail, onClose }: AnnounceModalProps): ReactElement {
  return (
    <ModalFrame uiId={`${pageId}-announce-modal`} open={open} title={detail?.subject ?? ''} onClose={onClose}>
      {detail === null ? null : (
        <div className="space-y-2">
          <p data-ui-id={`${pageId}-announce-date`} className="text-[13px] text-[#999]">
            {detail.startDateStr} ~ {detail.endDateStr}
          </p>
          <div className="flex">
            <span className="w-[100px] shrink-0 font-bold">公告單位：</span>
            <span data-ui-id={`${pageId}-announce-ref-name`}>{detail.refName}</span>
          </div>
          <div className="flex">
            <span className="w-[100px] shrink-0 font-bold">公告對象：</span>
            <span data-ui-id={`${pageId}-announce-obj-name`}>{detail.roleNames}</span>
          </div>
          <div className="flex">
            <span className="w-[100px] shrink-0 font-bold">相關網址：</span>
            <span data-ui-id={`${pageId}-announce-url`}>{detail.url === '' ? '無' : detail.url}</span>
          </div>
          <div className="flex">
            <span className="w-[100px] shrink-0 font-bold">公告內容：</span>
            <div data-ui-id={`${pageId}-announce-content`} className="break-all">
              {detail.content}
            </div>
          </div>
        </div>
      )}
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 待填表單 modal（todoFormModal）
// ---------------------------------------------------------------------------

/** 待填表單「訓練計畫/課程」欄的分流提示字（modal/todoForm.jsp:40-44 逐字）。 */
const TODO_HINT: Readonly<Record<string, string>> = {
  leave: '點選待簽核請假單連結後，會開啟請假單作業，即可進行假單簽核。',
  CoEditForm: '點選待簽核共同編輯表單連結後，會開啟共同編輯表單查詢功能，即可進行表單簽核。',
  studyRecord: '點選待簽核學習紀錄連結後，會開啟待簽核列表，即可進行表單簽核。',
  examList: '點選待批改線上測驗連結後，會開啟待批改列表，即可進行測驗卷批改。',
};

/** 表單名稱 cell（:17-25：＋號換空格＋` -formTargetTypeName`）。 */
function todoFormName(row: DashboardTodoForm): string {
  const base = (row.formTemplateName ?? '').replace(/\+/g, ' ');
  if (row.formTargetTypeName !== undefined && row.formTargetTypeName !== '') {
    return `${base} -${row.formTargetTypeName}`;
  }
  return base;
}

/** 訓練計畫/課程 cell（:40-56：leave/CoEditForm/studyRecord/examList 提示字；其餘 plan/className＋phaseName）。 */
function todoFormRefText(row: DashboardTodoForm): string {
  if (row.kind === 'leave') return TODO_HINT['leave'] ?? '';
  if (row.refTable === 'CoEditForm') return TODO_HINT['CoEditForm'] ?? '';
  if (row.refTable === 'studyRecord') return TODO_HINT['studyRecord'] ?? '';
  if (row.refTable === 'examList') return TODO_HINT['examList'] ?? '';
  const str = row.refTable === 'ClassInCourse' ? (row.className ?? '') : (row.planName ?? '');
  if (row.phaseName !== undefined && row.phaseName !== '') {
    return `${str}(${row.phaseName})`;
  }
  return str;
}

export interface TodoFormModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly todoForms: readonly DashboardTodoForm[];
  readonly onClose: () => void;
  /** 列點擊（a.showFill；modal/todoForm.jsp:90-110 的分流由呼叫端處理導航）。 */
  readonly onOpenForm: (row: DashboardTodoForm) => void;
}

/**
 * 待填表單 modal（modal/todoForm.jsp:112-145）：5 欄（表單名稱連結／訓練計畫|課程／學員／
 * 開始日／應完成日），order 應完成日 asc（DataTables order [4,'asc']——Table 無排序，
 * 以種子順序遞增呈現，D19-x 註記）；footer 關閉。
 */
export function TodoFormModal({ pageId, open, todoForms, onClose, onOpenForm }: TodoFormModalProps): ReactElement {
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'refText', header: '訓練計畫/課程', align: 'center' },
    { key: 'studentName', header: '學員', align: 'center' },
    { key: 'startDate', header: '開始日', align: 'center' },
    { key: 'dueDate', header: '應完成日', align: 'center' },
  ];
  const rows: readonly TableRow[] = [...todoForms]
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate))
    .map((row) => ({
      ...row,
      formTemplateName: todoFormName(row),
      refText: todoFormRefText(row),
    }));
  return (
    <ModalFrame uiId={`${pageId}-todo-form-modal`} open={open} title="待填表單" onClose={onClose} size="lg">
      <Table
        uiId={`${pageId}-todo-form-table`}
        columns={columns}
        rows={rows}
        rowKey={(row) => String(row['id'])}
        renderCell={(row, columnKey) => {
          if (columnKey === 'formTemplateName') {
            return (
              <a
                href="#"
                data-ui-id={`${pageId}-todo-form-link-${String(row['id'])}`}
                className="showFill cursor-pointer underline"
                onClick={(event) => {
                  event.preventDefault();
                  onOpenForm(row as unknown as DashboardTodoForm);
                }}
              >
                {String(row['formTemplateName'])}
              </a>
            );
          }
          const value = row[columnKey];
          return value === null || value === undefined ? '' : String(value);
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 臨床核心技能表單 modal（coreSkillFormModal）
// ---------------------------------------------------------------------------

export interface CoreSkillFormsModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly forms: readonly DashboardCoreSkillForm[];
  readonly onClose: () => void;
  /** 表單名稱連結（id>0 才有；showFillForm→form/prompt/fillForm）。 */
  readonly onOpenForm: (row: DashboardCoreSkillForm) => void;
}

/**
 * 臨床核心技能表單 modal（modal/coreSkillForms.jsp:106-127）：表單名稱（id>0 連結）／
 * 簽核狀態（第一個未完成 flow 的 accountName）／表單流程（genFormFlow）／表單狀態
 * （displayGrade）；score 為隱藏排序欄（Table 無排序，以 score asc 預排）。
 */
export function CoreSkillFormsModal({ pageId, open, forms, onClose, onOpenForm }: CoreSkillFormsModalProps): ReactElement {
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'signStatus', header: '簽核狀態', align: 'center' },
    { key: 'flowText', header: '表單流程', align: 'center' },
    { key: 'displayGrade', header: '表單狀態', align: 'center' },
  ];
  const rows: readonly TableRow[] = [...forms]
    .sort((a, b) => a.score - b.score)
    .map((form) => {
      const pendingAccount = form.isDone
        ? ''
        : (form.flowList.find((node) => !node.done)?.accountName ?? '');
      return {
        ...form,
        signStatus: pendingAccount,
        flowText: genFormFlow(form.flowList),
      };
    });
  return (
    <ModalFrame uiId={`${pageId}-coreskill-form-modal`} open={open} title="臨床核心技能表單" onClose={onClose} size="lg">
      <Table
        uiId={`${pageId}-coreskill-form-table`}
        columns={columns}
        rows={rows}
        rowKey={(row) => String(row['formTargetId'])}
        renderCell={(row, columnKey) => {
          if (columnKey === 'formTemplateName') {
            const form = row as unknown as DashboardCoreSkillForm;
            if (form.id > 0) {
              return (
                <a
                  href="#"
                  data-ui-id={`${pageId}-coreskill-form-link-${String(form.formTargetId)}`}
                  className="showFillForm cursor-pointer underline"
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenForm(form);
                  }}
                >
                  {form.formTemplateName}
                </a>
              );
            }
            return form.formTemplateName;
          }
          if (columnKey === 'flowText') {
            return <FormFlowText flowList={(row as unknown as DashboardCoreSkillForm).flowList} />;
          }
          const value = row[columnKey];
          return value === null || value === undefined ? '' : String(value);
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 等第制表單 modal（gpaFormModal——footer「Close」英文照實）
// ---------------------------------------------------------------------------

export interface GpaFormsModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly forms: readonly {
    readonly formTemplateName?: string | undefined;
    readonly displayGrade: string;
    readonly startDate?: string | undefined;
    readonly dueDate?: string | undefined;
    readonly flowList: DashboardFlowList;
  }[];
  readonly onClose: () => void;
  readonly onOpenForm: (row: (GpaFormsModalProps['forms'][number])) => void;
}

/**
 * 等第制表單 modal（modal/gpaForms.jsp:60-97）：表單名稱連結／等第（空＝無）／開始日／
 * 應完成日／表單流程；**footer「Close」英文照實**（legacy :96 btn 文案就是 Close）。
 */
export function GpaFormsModal({ pageId, open, forms, onClose, onOpenForm }: GpaFormsModalProps): ReactElement {
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'displayGrade', header: '等第', align: 'center' },
    { key: 'startDate', header: '開始日', align: 'center' },
    { key: 'dueDate', header: '應完成日', align: 'center' },
    { key: 'flowText', header: '表單流程', align: 'center' },
  ];
  const rows: readonly TableRow[] = forms.map((form, index) => ({
    ...form,
    displayGrade: form.displayGrade === '' ? '無' : form.displayGrade,
    flowText: genFormFlow(form.flowList),
    _idx: String(index),
  }));
  return (
    <ModalFrame uiId={`${pageId}-gpa-form-modal`} open={open} title="等第制表單" onClose={onClose} size="lg">
      <Table
        uiId={`${pageId}-gpa-form-table`}
        columns={columns}
        rows={rows}
        rowKey={(row) => `${pageId}-gpa-${String(row['_idx'])}`}
        renderCell={(row, columnKey) => {
          if (columnKey === 'formTemplateName') {
            return (
              <a
                href="#"
                data-ui-id={`${pageId}-gpa-form-link-${String(row['_idx'])}`}
                className="showFillForm cursor-pointer underline"
                onClick={(event) => {
                  event.preventDefault();
                  onOpenForm(row as unknown as GpaFormsModalProps['forms'][number]);
                }}
              >
                {String(row['formTemplateName'] ?? '')}
              </a>
            );
          }
          if (columnKey === 'flowText') {
            return <FormFlowText flowList={(row as unknown as GpaFormsModalProps['forms'][number]).flowList} />;
          }
          const value = row[columnKey];
          return value === null || value === undefined ? '' : String(value);
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 計畫表單 modal（MA；planFormsForMA.jsp:1-105）
// ---------------------------------------------------------------------------

export interface PlanFormsModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly forms: readonly DashboardMaPlanForm[];
  readonly onClose: () => void;
  readonly onOpenForm: (row: DashboardMaPlanForm) => void;
}

/**
 * 計畫表單 modal（MA 版 planForm 點擊；planFormsForMA.jsp）：表單名稱連結/開始日/應完成日/
 * 表單流程/表單狀態（isDone → 已完成/進行中）；order [4 desc, 2 asc]（:34）。
 */
export function PlanFormsModal({ pageId, open, forms, onClose, onOpenForm }: PlanFormsModalProps): ReactElement {
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'startDate', header: '開始日', align: 'center' },
    { key: 'dueDate', header: '應完成日', align: 'center' },
    { key: 'flowText', header: '表單流程', align: 'center' },
    { key: 'statusText', header: '表單狀態', align: 'center' },
  ];
  const sorted = [...forms].sort((a, b) => {
    const doneDiff = Number(b.isDone === true) - Number(a.isDone === true);
    if (doneDiff !== 0) return doneDiff;
    return a.startDate.localeCompare(b.startDate);
  });
  const rows: readonly TableRow[] = sorted.map((form, index) => ({
    ...form,
    statusText: form.isDone === true ? '已完成' : '進行中',
    flowText: genFormFlow(form.flowList),
    _idx: String(index),
  }));
  return (
    <ModalFrame uiId={`${pageId}-plan-form-modal`} open={open} title="計畫表單" onClose={onClose} size="lg">
      <Table
        uiId={`${pageId}-plan-form-table`}
        columns={columns}
        rows={rows}
        rowKey={(row) => `${pageId}-ma-plan-${String(row['_idx'])}`}
        renderCell={(row, columnKey) => {
          if (columnKey === 'formTemplateName') {
            const form = row as unknown as DashboardMaPlanForm;
            return (
              <a
                href="#"
                data-ui-id={`${pageId}-plan-form-link-${String(row['_idx'])}`}
                className="showFillForm cursor-pointer underline"
                onClick={(event) => {
                  event.preventDefault();
                  onOpenForm(form);
                }}
              >
                {form.formTemplateName}
              </a>
            );
          }
          if (columnKey === 'flowText') {
            return <FormFlowText flowList={(row as unknown as DashboardMaPlanForm).flowList} />;
          }
          const value = row[columnKey];
          return value === null || value === undefined ? '' : String(value);
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// EPA表單 modal（epaFormModal）
// ---------------------------------------------------------------------------

export interface EpaFormsModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly forms: readonly {
    readonly formTemplateName?: string | undefined;
    readonly displayGrade: string;
    readonly startDate?: string | undefined;
    readonly dueDate?: string | undefined;
    readonly flowList: DashboardFlowList;
    /** MA 版（epaFormsForMA.jsp）：表單狀態 欄用（已完成/進行中）。 */
    readonly isDone?: boolean | undefined;
  }[];
  /** MA 版欄位（epaFormsForMA.jsp:30-60：第 5 欄＝表單狀態 而非 平均分數；order 同為 [4 desc,2 asc]）。 */
  readonly maColumns?: boolean;
  readonly onClose: () => void;
  readonly onOpenForm: (row: (EpaFormsModalProps['forms'][number])) => void;
}

/** EPA表單 modal（modal/epaForms.jsp:79-107：表單名稱/平均分數/開始日/應完成日/表單流程；footer 關閉。
 *  MA 版（maColumns，epaFormsForMA.jsp:30-60）＝表單名稱/開始日/應完成日/表單流程/表單狀態。 */
export function EpaFormsModal({ pageId, open, forms, maColumns, onClose, onOpenForm }: EpaFormsModalProps): ReactElement {
  const columns: readonly TableColumn[] =
    maColumns === true
      ? [
          { key: 'formTemplateName', header: '表單名稱', align: 'center' },
          { key: 'startDate', header: '開始日', align: 'center' },
          { key: 'dueDate', header: '應完成日', align: 'center' },
          { key: 'flowText', header: '表單流程', align: 'center' },
          { key: 'statusText', header: '表單狀態', align: 'center' },
        ]
      : [
          { key: 'formTemplateName', header: '表單名稱', align: 'center' },
          { key: 'displayGrade', header: '平均分數', align: 'center' },
          { key: 'startDate', header: '開始日', align: 'center' },
          { key: 'dueDate', header: '應完成日', align: 'center' },
          { key: 'flowText', header: '表單流程', align: 'center' },
        ];
  const sorted =
    maColumns === true
      ? [...forms].sort((a, b) => {
          // order [[4,'desc'],[2,'asc']]（epaFormsForMA.jsp:31；欄 4＝表單狀態、欄 2＝開始日）。
          const doneDiff = Number(b.isDone === true) - Number(a.isDone === true);
          if (doneDiff !== 0) return doneDiff;
          return (a.startDate ?? '').localeCompare(b.startDate ?? '');
        })
      : forms;
  const rows: readonly TableRow[] = sorted.map((form, index) => ({
    ...form,
    displayGrade: form.displayGrade === '' ? '無' : form.displayGrade,
    statusText: form.isDone === true ? '已完成' : '進行中',
    flowText: genFormFlow(form.flowList),
    _idx: String(index),
  }));
  return (
    <ModalFrame uiId={`${pageId}-epa-form-modal`} open={open} title="EPA表單" onClose={onClose} size="lg">
      <Table
        uiId={`${pageId}-epa-form-table`}
        columns={columns}
        rows={rows}
        rowKey={(row) => `${pageId}-epa-${String(row['_idx'])}`}
        renderCell={(row, columnKey) => {
          if (columnKey === 'formTemplateName') {
            return (
              <a
                href="#"
                data-ui-id={`${pageId}-epa-form-link-${String(row['_idx'])}`}
                className="showFillForm cursor-pointer underline"
                onClick={(event) => {
                  event.preventDefault();
                  onOpenForm(row as unknown as EpaFormsModalProps['forms'][number]);
                }}
              >
                {String(row['formTemplateName'] ?? '')}
              </a>
            );
          }
          if (columnKey === 'flowText') {
            return <FormFlowText flowList={(row as unknown as EpaFormsModalProps['forms'][number]).flowList} />;
          }
          const value = row[columnKey];
          return value === null || value === undefined ? '' : String(value);
        }}
      />
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 個人行程 modal（addCalendarModal：新增/修改個人行程）
// ---------------------------------------------------------------------------

/** 共同成員候選池（accountKeyword autocomplete 不鏡像——D19-x；以候選下拉呈現）【雛形值待實機校準】。 */
export const SCHEDULE_ACCOUNT_OPTIONS = [
  { value: 'T0002', label: '李大明' },
  { value: 'T0003', label: '陳中明' },
  { value: 'T0004', label: '張小明' },
] as const;

export interface ScheduleModalProps {
  readonly pageId: string;
  readonly open: boolean;
  /** 新增（null＝新增個人行程標題）或編輯（傳入現值＋標題變 修改個人行程，:7/:223）。 */
  readonly schedule: DashboardMySchedule | null;
  readonly onClose: () => void;
  readonly onSave: (values: DashboardMySchedule) => void;
  readonly onDelete: () => void;
  readonly onApprove: (isEnable: 1 | -1) => void;
}

/**
 * 個人行程 modal（addCalendarModal.jsp:308-377）：*主題 content／hidden id+groupKey／
 * *行程開始時間 scheduleStartDate／*行程結束時間 scheduleEndDate／*備註 memo textarea／
 * *其他成員 accountKeyword（autocomplete 不鏡像＝候選下拉）＋accountId＋加入／others[] 多選＋
 * 移除（提示字 :371 逐字）；footer 關閉/刪除/存檔＋待決定時 婉拒/接受（:363-366 notDecide）。
 * 存檔驗證（:203-209）：欄位必填＋結束時間不得小於開始時間（逐字）。
 */
export function ScheduleModal({ pageId, open, schedule, onClose, onSave, onDelete, onApprove }: ScheduleModalProps): ReactElement {
  const dialogs = useDialogs();
  const [content, setContent] = useState(schedule?.content ?? '');
  const [startDate, setStartDate] = useState(schedule?.scheduleStartDateString ?? '');
  const [endDate, setEndDate] = useState(schedule?.scheduleEndDateString ?? '');
  const [memo, setMemo] = useState(schedule?.memo ?? '');
  const [keyword, setKeyword] = useState('');
  const [accountId, setAccountId] = useState('');
  const [others, setOthers] = useState<readonly { value: string; label: string }[]>(
    schedule === null ? [] : SCHEDULE_ACCOUNT_OPTIONS.filter((option) => option.value === schedule.groupKey),
  );
  const [errors, setErrors] = useState<readonly string[]>([]);
  const isUpdate = schedule !== null;

  function save(): void {
    const nextErrors: string[] = [];
    if (content === '') nextErrors.push('主題：此欄位為必填欄位【雛形值待實機校準】');
    if (startDate === '') nextErrors.push('行程開始時間：此欄位為必填欄位【雛形值待實機校準】');
    if (endDate === '') nextErrors.push('行程結束時間：此欄位為必填欄位【雛形值待實機校準】');
    if (memo === '') nextErrors.push('備註：此欄位為必填欄位【雛形值待實機校準】');
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }
    if (new Date(startDate).getTime() > new Date(endDate).getTime()) {
      // addCalendarModal.jsp:207 showAlertDialog('結束時間不得小於開始時間')。
      void dialogs.alert('結束時間不得小於開始時間');
      return;
    }
    setErrors([]);
    onSave({
      id: schedule?.id ?? '',
      content,
      scheduleStartDateString: startDate,
      scheduleEndDateString: endDate,
      memo,
      groupKey: others[0]?.value ?? '',
    });
  }

  function addToOthers(): void {
    const selected = SCHEDULE_ACCOUNT_OPTIONS.find((option) => option.value === accountId);
    if (selected === undefined) {
      return;
    }
    if (others.some((existing) => existing.value === selected.value)) {
      // addCalendarModal.jsp:280 showAlertDialog('成員重覆,無法加入!')。
      void dialogs.alert('成員重覆,無法加入!');
      return;
    }
    setOthers((current) => [...current, selected]);
  }

  function removeFromOthers(): void {
    setOthers((current) => current.filter((option) => option.value !== accountId));
  }

  return (
    <ModalFrame
      uiId={`${pageId}-schedule-modal`}
      open={open}
      title={isUpdate ? '修改個人行程' : '新增個人行程'}
      onClose={onClose}
      size="lg"
      footer={
        <div className="flex gap-2">
          <Button uiId={`${pageId}-schedule-close`} onClick={() => onClose()}>
            關閉
          </Button>
          <Button uiId={`${pageId}-schedule-delete`} variant="danger" onClick={() => onDelete()}>
            刪除
          </Button>
          <Button uiId={`${pageId}-schedule-save`} variant="primary" onClick={() => save()}>
            存檔
          </Button>
          {schedule?.isDecide === true ? (
            <>
              <Button uiId={`${pageId}-schedule-reject`} variant="danger" onClick={() => onApprove(-1)}>
                婉拒
              </Button>
              <Button uiId={`${pageId}-schedule-accept`} variant="primary" onClick={() => onApprove(1)}>
                接受
              </Button>
            </>
          ) : null}
        </div>
      }
    >
      <div className="space-y-3">
        {errors.length > 0 ? (
          <div data-ui-id={`${pageId}-schedule-errors`} className="text-danger text-[13px] text-[#d9534f]">
            {errors.map((error, index) => (
              <p key={index}>{error}</p>
            ))}
          </div>
        ) : null}
        <div className="flex items-center">
          <span className="w-[130px] shrink-0 text-right font-bold text-[#d9534f]">*</span>
          <span className="w-[70px] shrink-0 font-bold">主題：</span>
          <TextInput
            uiId={`${pageId}-schedule-field-content`}
            name="content"
            value={content}
            onChange={setContent}
            invalid={content === ''}
          />
        </div>
        <input type="hidden" name="id" value={schedule?.id ?? ''} />
        <input type="hidden" name="groupKey" value={others[0]?.value ?? ''} />
        <div className="flex items-center">
          <span className="w-[130px] shrink-0 text-right font-bold text-[#d9534f]">*</span>
          <span className="w-[70px] shrink-0 font-bold">行程開始時間：</span>
          <TextInput
            uiId={`${pageId}-schedule-field-schedule-start-date`}
            name="scheduleStartDate"
            value={startDate}
            onChange={setStartDate}
            placeholder="yyyy/MM/dd HH:mm"
            invalid={startDate === ''}
          />
        </div>
        <div className="flex items-center">
          <span className="w-[130px] shrink-0 text-right font-bold text-[#d9534f]">*</span>
          <span className="w-[70px] shrink-0 font-bold">行程結束時間：</span>
          <TextInput
            uiId={`${pageId}-schedule-field-schedule-end-date`}
            name="scheduleEndDate"
            value={endDate}
            onChange={setEndDate}
            placeholder="yyyy/MM/dd HH:mm"
            invalid={endDate === ''}
          />
        </div>
        <div className="flex items-start">
          <span className="w-[130px] shrink-0 text-right font-bold text-[#d9534f]">*</span>
          <span className="w-[70px] shrink-0 pt-1 font-bold">備註：</span>
          <Textarea
            uiId={`${pageId}-schedule-field-memo`}
            name="memo"
            value={memo}
            onChange={setMemo}
            invalid={memo === ''}
          />
        </div>
        <div className="flex items-start">
          <span className="w-[130px] shrink-0 text-right font-bold text-[#d9534f]">*</span>
          <span className="w-[70px] shrink-0 pt-1 font-bold">其他成員：</span>
          <div className="flex-1 space-y-2">
            <div className="flex gap-2">
              <TextInput
                uiId={`${pageId}-schedule-field-account-keyword`}
                name="accountKeyword"
                value={keyword}
                onChange={setKeyword}
                placeholder="(請輸入員工姓名或員工編號)"
              />
              <Select
                uiId={`${pageId}-schedule-field-account-id`}
                name="accountId"
                value={accountId}
                onChange={setAccountId}
                options={[...SCHEDULE_ACCOUNT_OPTIONS]}
                placeholder="請選擇"
              />
              <Button uiId={`${pageId}-schedule-add-others`} variant="primary" onClick={() => addToOthers()}>
                加入
              </Button>
            </div>
            <div className="flex gap-2">
              <select
                data-ui-id={`${pageId}-schedule-field-others`}
                name="others[]"
                multiple
                size={8}
                className="w-2/3 border border-border p-1"
                onChange={() => undefined}
              >
                {others.map((option) => (
                  <option key={option.value} value={option.value} data-ui-id={`${pageId}-schedule-others-${option.value}`}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div>
                <Button uiId={`${pageId}-schedule-remove-others`} variant="danger" onClick={() => removeFromOthers()}>
                  移除
                </Button>
                <p className="text-[12px]">若要移除,請點選左側成員,再按[移除]的按鈕</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 檢視等第制成績分布 modal（studentGpaGradeModal；modal/studentGpaGrade.jsp:67-165＋
// studentGPAGrade.js）
// ---------------------------------------------------------------------------

export interface StudentGpaGradeModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly detail: TeacherStudentDetail;
  /** 等第制報表（main/ugy/getStudentGPAFormReport 種子；查詢鈕重取＝同種子，D19-x）。 */
  readonly report: GpaFormReport;
  readonly onClose: () => void;
  readonly onOpenForm: (row: DashboardGradeReportRow) => void;
}

/**
 * 檢視等第制成績分布（modal/studentGpaGrade.jsp）：查詢區（startDate/endDate 預填
 * currentPhase＋查詢/resetTime）＋學員卡（studentName+grade／deptName／照片 placeholder／
 * gpaLight 燈）＋個人等第制成績分布情形表（分數/D/C/B/A-/A/A+/A++/個人平均/全學生平均，
 * 無值＝無資料）＋說明兩行（:147-148 逐字）＋學年(期)實習科別等第制成績列表 9 欄
 * （表單名稱連結 showFillForm）＋footer 關閉。
 */
export function StudentGpaGradeModal({ pageId, open, detail, report, onClose, onOpenForm }: StudentGpaGradeModalProps): ReactElement {
  const currentPhase = detail.phaseList.find((phase) => phase.currentPhase);
  const [startDate, setStartDate] = useState(currentPhase?.startDate ?? '');
  const [endDate, setEndDate] = useState(currentPhase?.endDate ?? '');
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'planTargetName', header: '訓練計畫', align: 'center' },
    { key: 'groupName', header: '組別', align: 'center' },
    { key: 'subPhaseName', header: '階段/子階段', align: 'center' },
    { key: 'phaseDepartmentName', header: '訓練階段科部', align: 'center' },
    { key: 'phasePeriod', header: '訓練階段期間', align: 'center' },
    { key: 'dueDate', header: '應完成日期', align: 'center' },
    { key: 'flowText', header: '表單流程', align: 'center' },
    { key: 'displayGrade', header: '成績結果', align: 'center' },
  ];
  const rows: readonly TableRow[] = report.gpaForms.map((row, index) => ({
    ...row,
    subPhaseName: row.phaseName ? `${row.phaseName}${row.subPhaseName}` : row.subPhaseName,
    phasePeriod: `${row.phaseStartDate} ~ ${row.phaseEndDate}`,
    displayGrade: row.displayGrade === '' ? '-' : row.displayGrade,
    flowText: genFormFlow(row.flowList),
    _idx: String(index),
  }));
  return (
    <ModalFrame uiId={`${pageId}-student-gpa-grade-modal`} open={open} title="檢視等第制成績分布" onClose={onClose} size="lg">
      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <div>
            <p className="text-[12px]">開始日期</p>
            <TextInput uiId={`${pageId}-gpa-grade-field-start-date`} name="startDate" value={startDate} onChange={setStartDate} />
          </div>
          <div>
            <p className="text-[12px]">結束日期</p>
            <TextInput uiId={`${pageId}-gpa-grade-field-end-date`} name="endDate" value={endDate} onChange={setEndDate} />
          </div>
          <Button uiId={`${pageId}-gpa-grade-query`} onClick={() => undefined}>
            查詢
          </Button>
        </div>
        <div className="flex gap-4">
          <div className="text-center">
            <div className="mx-auto h-[64px] w-[64px] rounded-full bg-[#e8e8e8]" title="getPhoto placeholder" />
            <p data-ui-id={`${pageId}-gpa-grade-student-name`} className="text-[14px] font-bold">
              {`${detail.studentName} ${detail.grade}`}
            </p>
            <p data-ui-id={`${pageId}-gpa-grade-dept-name`}>{currentPhase?.phaseDeptName ?? ''}</p>
            <span
              data-ui-id={`${pageId}-gpa-grade-light`}
              className={`ml-2 inline-block h-[14px] w-[14px] rounded-full ${LIGHT_COLOR_CLASS[detail.gpaLightColor]}`}
            />
          </div>
          <div className="flex-1">
            <table data-ui-id={`${pageId}-gpa-grade-table`} className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  <th colSpan={10} className="border border-border p-1 text-center font-bold">
                    個人等第制成績分布情形
                  </th>
                </tr>
                <tr>
                  {['分數', 'D', 'C', 'B', 'A-', 'A', 'A+', 'A++', '個人平均', '全學生平均'].map((header) => (
                    <th key={header} className="border border-border p-1 text-center font-bold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border p-1 text-center">次數</td>
                  {(['D', 'C', 'B', 'A-', 'A', 'A+', 'A++'] as const).map((key, gradeIndex) => (
                    <td key={key} data-ui-id={`${pageId}-gpa-grade-count-${gradeIndex}`} className="border border-border p-1 text-center">
                      {report.gradeCountMap[key]}
                    </td>
                  ))}
                  <td className="border border-border p-1 text-center">{report.avgScore ?? '無資料'}</td>
                  <td className="border border-border p-1 text-center">{report.allStudentAvgScore ?? '無資料'}</td>
                </tr>
              </tbody>
            </table>
            <p className="mt-2 text-[12px]">
              <span className="font-bold">說明：</span>
            </p>
            <p className="text-[12px]">
              <span className="font-bold">成績：</span>平均成績/該計畫所有學員平均
            </p>
            <p className="text-[12px]">
              <span className="font-bold">燈號：</span>該學年等第制成績：一次B以下亮黃燈，兩次以上亮紅燈。
            </p>
          </div>
        </div>
        <h3 className="text-[15px] font-bold">學年(期)實習科別等第制成績列表</h3>
        <Table
          uiId={`${pageId}-gpa-grade-forms-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => `${pageId}-gpa-forms-${String(row['_idx'])}`}
          renderCell={(row, columnKey) => {
            if (columnKey === 'formTemplateName') {
              const form = row as unknown as DashboardGradeReportRow;
              return (
                <a
                  href="#"
                  data-ui-id={`${pageId}-gpa-grade-form-link-${String(row['_idx'])}`}
                  className="showFillForm cursor-pointer underline"
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenForm(form);
                  }}
                >
                  {form.formTemplateName}
                </a>
              );
            }
            if (columnKey === 'flowText') {
              return <FormFlowText flowList={(row as unknown as DashboardGradeReportRow).flowList} />;
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </div>
    </ModalFrame>
  );
}

// ---------------------------------------------------------------------------
// 檢視EPA成績分布 modal（studentEpaGradeModal；modal/studentEpaGrade.jsp:164-261＋
// studentEpaGrade.js）
// ---------------------------------------------------------------------------

export interface StudentEpaGradeModalProps {
  readonly pageId: string;
  readonly open: boolean;
  readonly detail: TeacherStudentDetail;
  /** EPA報表（main/ugy/getStudentEPAFormReport 種子；查詢鈕重取＝同種子，D19-x）。 */
  readonly report: EpaFormReport;
  readonly onClose: () => void;
  readonly onOpenForm: (row: EpaFormReport['epaForms'][number]['form']) => void;
}

/**
 * 檢視EPA成績分布（modal/studentEpaGrade.jsp）：查詢區＋學員卡（epaLight）＋EPA 成績分布圖
 * （每項目一列 epaTitle＋1~5 分寬度條（data-score，clickable → 表單列過濾））＋
 * epaScoreTable（EPA項目/測驗次數/平均/全學員平均）＋學年(期)EPA評量表單列表 10 欄（含隐藏
 * epaScoreMap）＋footer 關閉。分數點擊過濾＝legacy dataTable ext.search，雛形以 state 過濾。
 */
export function StudentEpaGradeModal({ pageId, open, detail, report, onClose, onOpenForm }: StudentEpaGradeModalProps): ReactElement {
  const currentPhase = detail.phaseList.find((phase) => phase.currentPhase);
  const [startDate, setStartDate] = useState(currentPhase?.startDate ?? '');
  const [endDate, setEndDate] = useState(currentPhase?.endDate ?? '');
  const [scoreFilter, setScoreFilter] = useState<{ column: string; score: number } | null>(null);
  const total = (scoreCountMap: DashboardEpaGradeBean['scoreCountMap']): number =>
    Object.values(scoreCountMap).reduce((sum, count) => sum + (count ?? 0), 0);
  const percent = (count: number, all: number): number => (all === 0 ? 0 : Math.floor((count * 100) / all * 100) / 100);
  const columns: readonly TableColumn[] = [
    { key: 'formTemplateName', header: '表單名稱', align: 'center' },
    { key: 'planTargetName', header: '訓練計畫', align: 'center' },
    { key: 'groupName', header: '組別', align: 'center' },
    { key: 'subPhaseName', header: '階段/子階段', align: 'center' },
    { key: 'phaseDepartmentName', header: '訓練階段科部', align: 'center' },
    { key: 'phasePeriod', header: '訓練階段期間', align: 'center' },
    { key: 'dueDate', header: '應完成日期', align: 'center' },
    { key: 'flowText', header: '表單流程', align: 'center' },
    { key: 'displayGrade', header: '平均成績', align: 'center' },
  ];
  const rows: readonly TableRow[] = report.epaForms
    .filter((entry) =>
      scoreFilter === null
        ? true
        : (entry.epaScoreMap[scoreFilter.column] ?? 0) > 0 && entry.epaScoreMap[scoreFilter.column] === scoreFilter.score,
    )
    .map((entry, index) => ({
      ...entry.form,
      subPhaseName: entry.form.phaseName ? `${entry.form.phaseName}${entry.form.subPhaseName}` : entry.form.subPhaseName,
      phasePeriod: `${entry.form.phaseStartDate} ~ ${entry.form.phaseEndDate}`,
      displayGrade: entry.form.displayGrade === '' ? '-' : entry.form.displayGrade,
      flowText: genFormFlow(entry.form.flowList),
      _idx: String(index),
    }));
  return (
    <ModalFrame uiId={`${pageId}-student-epa-grade-modal`} open={open} title="檢視EPA成績分布" onClose={onClose} size="lg">
      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <div>
            <p className="text-[12px]">開始日期</p>
            <TextInput uiId={`${pageId}-epa-grade-field-start-date`} name="startDate" value={startDate} onChange={setStartDate} />
          </div>
          <div>
            <p className="text-[12px]">結束日期</p>
            <TextInput uiId={`${pageId}-epa-grade-field-end-date`} name="endDate" value={endDate} onChange={setEndDate} />
          </div>
          <Button uiId={`${pageId}-epa-grade-query`} onClick={() => undefined}>
            查詢
          </Button>
        </div>
        <div className="flex gap-4">
          <div className="text-center">
            <div className="mx-auto h-[64px] w-[64px] rounded-full bg-[#e8e8e8]" title="getPhoto placeholder" />
            <p data-ui-id={`${pageId}-epa-grade-student-name`} className="text-[14px] font-bold">
              {`${detail.studentName} ${detail.grade}`}
            </p>
            <p data-ui-id={`${pageId}-epa-grade-dept-name`}>{currentPhase?.phaseDeptName ?? ''}</p>
            <span
              data-ui-id={`${pageId}-epa-grade-light`}
              className={`ml-2 inline-block h-[14px] w-[14px] rounded-full ${LIGHT_COLOR_CLASS[detail.epaLightColor]}`}
            />
          </div>
          <div className="flex-1 space-y-2">
            <div data-ui-id={`${pageId}-epa-scores`} className="space-y-1">
              {Object.keys(report.epaGradeMap).map((key, rowIndex) => {
                const bean = report.epaGradeMap[key];
                if (bean === undefined) return null;
                const all = total(bean.scoreCountMap);
                return (
                  <div key={key} data-ui-id={`${pageId}-epa-score-row-${rowIndex}`} className="epaScoreRow">
                    <div className="text-[12px] font-bold" title={report.epaTitleMap[key]}>{key}</div>
                    <div className="flex">
                      {(['1.0', '2.0', '3.0', '4.0', '5.0'] as const).map((scoreKey) => {
                        const count = bean.scoreCountMap[scoreKey] ?? 0;
                        if (count === 0) return null;
                        const score = Number(scoreKey.split('.')[0]);
                        return (
                          <div
                            key={scoreKey}
                            data-ui-id={`${pageId}-epa-score-${rowIndex}-${score}`}
                            data-score={score}
                            className="cursor-pointer border-r border-white text-center text-[11px] text-white"
                            style={{ width: `${percent(count, all)}%`, backgroundColor: SCORE_BAR_COLOR[score] }}
                            title={`${key} ${score} 分：${count} 筆（點擊過濾表單列表）`}
                            onClick={() => setScoreFilter({ column: key, score })}
                          >
                            {count}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <table data-ui-id={`${pageId}-epa-score-table`} className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {['EPA項目', '測驗次數', '平均', '全學員平均'].map((header) => (
                    <th key={header} className="border border-border p-1 text-center font-bold">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(report.epaGradeMap).map((key, rowIndex) => {
                  const bean = report.epaGradeMap[key];
                  if (bean === undefined) return null;
                  return (
                    <tr key={key} data-ui-id={`${pageId}-epa-score-avg-${rowIndex}`}>
                      <td className="border border-border p-1">{key}</td>
                      <td className="border border-border p-1 text-center">{bean.testingCount}</td>
                      <td className="border border-border p-1 text-center">{bean.studentAvgScore ?? 0}</td>
                      <td className="border border-border p-1 text-center">{bean.avgScore}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <h3 className="text-[15px] font-bold">學年(期)EPA評量表單列表</h3>
        <Table
          uiId={`${pageId}-epa-grade-forms-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => `${pageId}-epa-forms-${String(row['_idx'])}`}
          renderCell={(row, columnKey) => {
            if (columnKey === 'formTemplateName') {
              return (
                <a
                  href="#"
                  data-ui-id={`${pageId}-epa-grade-form-link-${String(row['_idx'])}`}
                  className="showFillForm cursor-pointer underline"
                  onClick={(event) => {
                    event.preventDefault();
                    onOpenForm(row as unknown as EpaFormReport['epaForms'][number]['form']);
                  }}
                >
                  {String(row['formTemplateName'])}
                </a>
              );
            }
            if (columnKey === 'flowText') {
              return <FormFlowText flowList={(row as unknown as DashboardGradeReportRow).flowList} />;
            }
            const value = row[columnKey];
            return value === null || value === undefined ? '' : String(value);
          }}
        />
      </div>
    </ModalFrame>
  );
}
